import { forwardRef, ReactNode, Ref } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  SxProps,
  Typography
} from '@mui/material';
import { Theme } from '@mui/system';

// types
interface SubCardProps {
  children: ReactNode | string | null;
  content?: boolean;
  contentClass?: string;
  darkTitle?: boolean;
  secondary?: ReactNode | string;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  cardHeaderSx?: SxProps<Theme>;
  title?: ReactNode | string;
  divider?: boolean;
}

// ==============================|| CUSTOM SUB CARD ||============================== //

const SubCard = forwardRef(
  (
    {
      children,
      content,
      contentClass,
      darkTitle,
      secondary,
      sx = {},
      cardHeaderSx = {},
      contentSx = {},
      title,
      divider,
      ...others
    }: SubCardProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const theme = useTheme();

    return (
      <Card
        ref={ref}
        sx={{
          border: '1px solid',
          borderColor:
            theme.palette.mode === 'dark'
              ? theme.palette.dark.light + 15
              : theme.palette.grey[200],
          ':hover': {
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 2px 14px 0 rgb(33 150 243 / 10%)'
                : '0 2px 14px 0 rgb(32 40 45 / 8%)'
          },
          ...sx
        }}
        {...others}
      >
        {/* card header and action */}
        {!darkTitle && title && (
          <CardHeader
            sx={{ p: 2.5, ...cardHeaderSx }}
            title={<Typography variant='h5'>{title}</Typography>}
            action={secondary}
          />
        )}
        {darkTitle && title && (
          <CardHeader
            sx={{ p: 2.5, ...cardHeaderSx }}
            title={<Typography variant='h4'>{title}</Typography>}
            action={secondary}
          />
        )}

        {/* content & header divider */}
        {divider && title && (
          <Divider
            sx={{
              opacity: 1,
              borderColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.dark.light + 15
                  : theme.palette.grey[200]
            }}
          />
        )}

        {/* card content */}
        {content && (
          <CardContent
            sx={{ p: 2.5, ...contentSx }}
            className={contentClass || ''}
          >
            {children}
          </CardContent>
        )}
        {!content && children}
      </Card>
    );
  }
);

SubCard.defaultProps = {
  content: true,
  divider: true
};

export default SubCard;
